import React, { useState } from 'react';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, Globe, Users, Briefcase, MapPin, Award,
  Plane, Star, Info, ExternalLink, CheckCircle
} from 'lucide-react';

export default function CabinCrewAirlines() {
  const [selectedAirline, setSelectedAirline] = useState('THY');

  const airlines = {
    THY: {
      name: 'Türk Hava Yolları',
      logo: '🇹🇷',
      color: 'from-red-500 to-red-700',
      slogan: 'Widen Your World',
      founded: 1933,
      hub: 'İstanbul Havalimanı (IST)',
      destinations: '340+ destinasyon',
      fleet: '400+ uçak',
      alliance: 'Star Alliance',
      website: 'https://www.turkishairlines.com/tr-tr/kariyer/',
      overview: 'Türk Hava Yolları, 1933 yılında kurulan Türkiye\'nin bayrak taşıyıcı havayoludur. Dünyanın en çok ülkeye uçan havayolu şirketi unvanını taşımaktadır.',
      cabinService: {
        approach: 'Türk misafirperverliği ve modern servis anlayışı',
        classes: ['Economy', 'Business', 'First (Bazı rotalarda)'],
        highlights: [
          'Türk mutfağından özel menüler',
          'Flying Chef servisi (Business)',
          'Lounge erişimi',
          'Geniş IFE seçenekleri'
        ]
      },
      uniform: {
        designer: 'Ettore Bilotta',
        colors: ['Kırmızı', 'Lacivert', 'Beyaz'],
        style: 'Modern ve şık, Türk motiflerinden ilham alan tasarım'
      },
      hiring: {
        requirements: [
          'Türkiye vatandaşlığı veya çalışma izni',
          'Kadın: 160-180 cm, Erkek: 168-190 cm',
          'İyi derecede İngilizce (ve tercihen 2. dil)',
          'Yüzme bilmek zorunlu',
          'En az lise mezunu'
        ],
        process: [
          'Online başvuru',
          'İngilizce sınavı',
          'Grup mülakatı',
          'Bireysel mülakat',
          'Sağlık kontrolü',
          'Eğitim (8-10 hafta)'
        ],
        benefits: [
          'Uluslararası kariyer fırsatı',
          'Ücretsiz uçuş hakkı',
          'Sağlık sigortası',
          'Prim sistemi'
        ]
      }
    },
    Pegasus: {
      name: 'Pegasus Hava Yolları',
      logo: '🟠',
      color: 'from-orange-500 to-yellow-500',
      slogan: 'Herkes Uçabilmeli',
      founded: 1990,
      hub: 'Sabiha Gökçen Havalimanı (SAW)',
      destinations: '120+ destinasyon',
      fleet: '100+ uçak',
      alliance: '-',
      website: 'https://www.flypgs.com/kariyer',
      overview: 'Pegasus, Türkiye\'nin öncü düşük maliyetli havayolu şirketidir. Ekonomik fiyatlarla kaliteli hizmet sunmayı hedefler.',
      cabinService: {
        approach: 'Hızlı, verimli ve müşteri odaklı',
        classes: ['Economy'],
        highlights: [
          'Ücretli ikram servisi',
          'Hızlı boarding',
          'Mobil check-in',
          'BolBol puan programı'
        ]
      },
      uniform: {
        designer: '-',
        colors: ['Turuncu', 'Lacivert'],
        style: 'Dinamik ve genç'
      },
      hiring: {
        requirements: [
          'Türkiye vatandaşı',
          'Kadın: 160-180 cm, Erkek: 168-190 cm',
          'İyi derecede İngilizce',
          'En az lise mezunu'
        ],
        process: [
          'Online başvuru',
          'Telefon görüşmesi',
          'Yüz yüze mülakat',
          'Sağlık kontrolü',
          'Eğitim'
        ],
        benefits: [
          'Genç ve dinamik çalışma ortamı',
          'Uçuş indirimleri',
          'Kariyer gelişim fırsatları'
        ]
      }
    },
    SunExpress: {
      name: 'SunExpress',
      logo: '☀️',
      color: 'from-yellow-500 to-orange-500',
      slogan: 'Fly to Smile',
      founded: 1989,
      hub: 'Antalya Havalimanı (AYT)',
      destinations: '90+ destinasyon',
      fleet: '90+ uçak',
      alliance: '-',
      website: 'https://www.sunexpress.com/tr/kariyer/',
      overview: 'SunExpress, THY ve Lufthansa ortaklığında kurulan, Türkiye-Almanya hattında güçlü bir havayoludur.',
      cabinService: {
        approach: 'Güler yüzlü ve profesyonel',
        classes: ['Economy', 'SunPremium'],
        highlights: [
          'Almanya ve Türkiye bağlantılı uçuşlar',
          'Tatil destinasyonlarına odaklı',
          'Kaliteli yemek servisi'
        ]
      },
      uniform: {
        designer: '-',
        colors: ['Sarı', 'Lacivert'],
        style: 'Enerjik ve sıcak'
      },
      hiring: {
        requirements: [
          'Türkiye veya Almanya vatandaşı/çalışma izni',
          'Kadın: 160-180 cm, Erkek: 168-188 cm',
          'İngilizce ve tercihen Almanca',
          'En az lise mezunu'
        ],
        process: [
          'Online başvuru',
          'Assessment center',
          'Mülakat',
          'Sağlık kontrolü',
          'Eğitim'
        ],
        benefits: [
          'Türk-Alman ortaklığının avantajları',
          'Uluslararası çalışma imkanı',
          'Cazip sosyal haklar'
        ]
      }
    },
    Emirates: {
      name: 'Emirates',
      logo: '🇦🇪',
      color: 'from-red-600 to-red-800',
      slogan: 'Fly Better',
      founded: 1985,
      hub: 'Dubai Uluslararası Havalimanı (DXB)',
      destinations: '150+ destinasyon',
      fleet: '260+ uçak',
      alliance: '-',
      website: 'https://www.emiratesgroupcareers.com/cabin-crew/',
      overview: 'Emirates, dünyanın en büyük uluslararası havayolu şirketlerinden biridir. Lüks hizmet anlayışıyla tanınır.',
      cabinService: {
        approach: 'Ultra lüks ve kişiselleştirilmiş hizmet',
        classes: ['Economy', 'Business', 'First'],
        highlights: [
          'ICE Entertainment sistemi',
          'A380 Onboard Lounge',
          'First Class Suites',
          'Dünya mutfağından menüler'
        ]
      },
      uniform: {
        designer: 'Çeşitli tasarımcılar',
        colors: ['Bej', 'Kırmızı'],
        style: 'İkonik kırmızı şapka ve zarif üniforma'
      },
      hiring: {
        requirements: [
          'Herhangi bir ülke vatandaşı olabilir',
          'En az 160 cm boy (parmak ucunda 212 cm uzanabilme)',
          'Mükemmel İngilizce',
          'En az lise mezunu',
          '21 yaş üstü'
        ],
        process: [
          'Open Day\'e katılım',
          'İngilizce testi',
          'Grup egzersizleri',
          'Final mülakatı',
          'Sağlık kontrolü',
          'Dubai\'de 7 hafta eğitim'
        ],
        benefits: [
          'Vergisiz maaş',
          'Ücretsiz konaklama Dubai\'de',
          'Unlimited uçuş hakkı',
          'Dünya çapında network'
        ]
      },
      culture: {
        title: 'Emirates Kültürü',
        points: [
          'Çok uluslu çalışma ortamı (150+ milletten çalışan)',
          'Lüks ve premium hizmet odaklı',
          'Yoğun çalışma temposu',
          'Dubai\'de yaşam zorunluluğu',
          'Sıkı grooming standartları'
        ]
      }
    },
    Qatar: {
      name: 'Qatar Airways',
      logo: '🇶🇦',
      color: 'from-purple-700 to-purple-900',
      slogan: 'Going Places Together',
      founded: 1993,
      hub: 'Hamad Uluslararası Havalimanı (DOH)',
      destinations: '170+ destinasyon',
      fleet: '230+ uçak',
      alliance: 'Oneworld',
      website: 'https://careers.qatarairways.com/cabin-crew',
      overview: 'Qatar Airways, Skytrax tarafından defalarca "Dünyanın En İyi Havayolu" seçilmiş, Körfez\'in önde gelen taşıyıcısıdır.',
      cabinService: {
        approach: '5 yıldızlı otel kalitesinde hizmet',
        classes: ['Economy', 'Business (Qsuite)', 'First'],
        highlights: [
          'Qsuite - dünyanın en iyi business class\'ı',
          'Dine on Demand',
          'Premium yemekler',
          'Geniş entertainment seçeneği'
        ]
      },
      uniform: {
        designer: '-',
        colors: ['Bordo', 'Gri'],
        style: 'Zarif ve sofistike'
      },
      hiring: {
        requirements: [
          'Herhangi bir ülke vatandaşı',
          'Kadın: 157 cm+, Erkek: 168 cm+',
          'Mükemmel İngilizce',
          'En az lise mezunu',
          '21 yaş üstü'
        ],
        process: [
          'Online başvuru',
          'Open Day veya Video mülakat',
          'Assessment',
          'Final mülakat',
          'Doha\'da eğitim'
        ],
        benefits: [
          'Vergisiz maaş',
          'Ücretsiz konaklama Doha\'da',
          'Uçuş hakları',
          'Sağlık sigortası'
        ]
      },
      culture: {
        title: 'Qatar Kültürü',
        points: [
          'Mükemmeliyetçi hizmet anlayışı',
          'Çok uluslu ortam',
          'Doha\'da yaşam',
          'Yüksek standartlar',
          'Kariyer gelişimi fırsatları'
        ]
      }
    },
    Etihad: {
      name: 'Etihad Airways',
      logo: '🇦🇪',
      color: 'from-amber-600 to-amber-800',
      slogan: 'Choose Well',
      founded: 2003,
      hub: 'Abu Dhabi Uluslararası Havalimanı (AUH)',
      destinations: '80+ destinasyon',
      fleet: '100+ uçak',
      alliance: '-',
      website: 'https://careers.etihad.com/',
      overview: 'Etihad Airways, BAE\'nin ulusal havayolu olup, yenilikçi ürünleri ve lüks hizmetiyle tanınır.',
      cabinService: {
        approach: 'Yenilikçi ve lüks',
        classes: ['Economy', 'Business', 'First', 'The Residence'],
        highlights: [
          'The Residence - uçan daire',
          'A380 First Apartments',
          'Flying Nanny servisi',
          'Food & Beverage Manager\'lı uçuşlar'
        ]
      },
      uniform: {
        designer: 'Ettore Bilotta',
        colors: ['Kahverengi', 'Bej'],
        style: 'Modern ve şık'
      },
      hiring: {
        requirements: [
          'Herhangi bir ülke vatandaşı',
          'En az 160 cm boy',
          'İyi İngilizce',
          'En az lise mezunu'
        ],
        process: [
          'Online başvuru',
          'Assessment Day',
          'Final mülakat',
          'Abu Dhabi\'de eğitim'
        ],
        benefits: [
          'Vergisiz maaş',
          'Ücretsiz konaklama',
          'Uçuş hakları',
          'Abu Dhabi\'de yaşam'
        ]
      }
    },
    Lufthansa: {
      name: 'Lufthansa',
      logo: '🇩🇪',
      color: 'from-blue-800 to-yellow-500',
      slogan: 'Say yes to the world',
      founded: 1953,
      hub: 'Frankfurt (FRA) & Münih (MUC)',
      destinations: '220+ destinasyon',
      fleet: '280+ uçak',
      alliance: 'Star Alliance (Kurucu)',
      website: 'https://www.be-lufthansa.com/',
      overview: 'Lufthansa, Avrupa\'nın en büyük havayolu şirketlerinden biri olup, Alman kalitesi ve güvenilirliğiyle tanınır.',
      cabinService: {
        approach: 'Alman hassasiyeti ve profesyonellik',
        classes: ['Economy', 'Premium Economy', 'Business', 'First'],
        highlights: [
          'First Class Terminal Frankfurt',
          'Michelin yıldızlı şeflerden menüler',
          'Yüksek kalite standartları'
        ]
      },
      uniform: {
        designer: '-',
        colors: ['Lacivert', 'Sarı'],
        style: 'Klasik ve profesyonel'
      },
      hiring: {
        requirements: [
          'AB vatandaşlığı veya çalışma izni',
          'Almanca ve İngilizce bilmek zorunlu',
          'En az lise mezunu',
          'Müşteri hizmetleri deneyimi tercih sebebi'
        ],
        process: [
          'Online başvuru',
          'Online testler',
          'Assessment center',
          'Mülakat',
          'Eğitim'
        ],
        benefits: [
          'Avrupa\'da kariyer',
          'Star Alliance avantajları',
          'Güçlü sosyal haklar',
          'Uzun vadeli iş güvencesi'
        ]
      }
    },
    BritishAirways: {
      name: 'British Airways',
      logo: '🇬🇧',
      color: 'from-blue-900 to-red-600',
      slogan: 'To Fly. To Serve.',
      founded: 1974,
      hub: 'London Heathrow (LHR)',
      destinations: '180+ destinasyon',
      fleet: '250+ uçak',
      alliance: 'Oneworld',
      website: 'https://careers.ba.com/',
      overview: 'British Airways, Birleşik Krallık\'ın bayrak taşıyıcısı olup, köklü havacılık geçmişiyle tanınır.',
      cabinService: {
        approach: 'İngiliz zarafeti ve profesyonellik',
        classes: ['Economy', 'Premium Economy', 'Business (Club World)', 'First'],
        highlights: [
          'Club World flat beds',
          'Arrivals Lounge',
          'Britanya mutfağından seçmeler',
          'Afternoon Tea servisi'
        ]
      },
      uniform: {
        designer: 'Julien MacDonald',
        colors: ['Lacivert', 'Kırmızı'],
        style: 'Klasik British elegance'
      },
      hiring: {
        requirements: [
          'UK çalışma izni',
          'İngilizce ana dil seviyesi',
          'En az 18 yaş',
          'Müşteri hizmetleri deneyimi'
        ],
        process: [
          'Online başvuru',
          'Video mülakat',
          'Assessment day',
          'Eğitim'
        ],
        benefits: [
          'Londra merkezli kariyer',
          'Oneworld avantajları',
          'Uçuş indirimleri',
          'Emeklilik planı'
        ]
      }
    }
  };

  const currentAirline = airlines[selectedAirline];

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50 p-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-800 flex items-center gap-3">
              <Building2 className="w-8 h-8 text-cyan-500" />
              Havayolu Şirketleri Bilgi Bankası
            </h1>
            <p className="text-gray-600">Başvurmak istediğiniz havayolunu detaylı tanıyın</p>
          </div>

          {/* Airline Selector */}
          <div className="flex flex-wrap gap-2 mb-6">
            {Object.entries(airlines).map(([key, airline]) => (
              <Button
                key={key}
                variant={selectedAirline === key ? 'default' : 'outline'}
                onClick={() => setSelectedAirline(key)}
                className={selectedAirline === key ? `bg-gradient-to-r ${airline.color}` : ''}
              >
                <span className="mr-2">{airline.logo}</span>
                {airline.name}
              </Button>
            ))}
          </div>

          {/* Airline Detail */}
          <div className="space-y-6">
            {/* Overview Card */}
            <Card className={`bg-gradient-to-r ${currentAirline.color} text-white`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div>
                    <h2 className="text-3xl font-black flex items-center gap-3">
                      <span className="text-4xl">{currentAirline.logo}</span>
                      {currentAirline.name}
                    </h2>
                    <p className="text-white/80 mt-1">&ldquo;{currentAirline.slogan}&rdquo;</p>
                  </div>
                  <Button 
                    variant="secondary" 
                    className="mt-4 md:mt-0"
                    onClick={() => window.open(currentAirline.website, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Kariyer Sayfası
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
                  <div>
                    <p className="text-white/60 text-sm">Kuruluş</p>
                    <p className="font-bold">{currentAirline.founded}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">Hub</p>
                    <p className="font-bold text-sm">{currentAirline.hub}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">Destinasyon</p>
                    <p className="font-bold">{currentAirline.destinations}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">Filo</p>
                    <p className="font-bold">{currentAirline.fleet}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">İttifak</p>
                    <p className="font-bold">{currentAirline.alliance}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cabin Service */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plane className="w-5 h-5 text-sky-500" />
                    Kabin Hizmet Yaklaşımı
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{currentAirline.cabinService.approach}</p>
                  <div className="mb-4">
                    <p className="font-medium mb-2">Sınıflar:</p>
                    <div className="flex flex-wrap gap-2">
                      {currentAirline.cabinService.classes.map((cls, i) => (
                        <Badge key={i} variant="outline">{cls}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="font-medium mb-2">Öne Çıkan Özellikler:</p>
                    <ul className="space-y-1">
                      {currentAirline.cabinService.highlights.map((h, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <Star className="w-4 h-4 text-amber-500" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Uniform */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-500" />
                    Üniforma
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {currentAirline.uniform.designer !== '-' && (
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">Tasarımcı:</span> {currentAirline.uniform.designer}
                    </p>
                  )}
                  <div className="mb-4">
                    <p className="font-medium mb-2">Renkler:</p>
                    <div className="flex gap-2">
                      {currentAirline.uniform.colors.map((color, i) => (
                        <Badge key={i} variant="secondary">{color}</Badge>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-600">
                    <span className="font-medium">Stil:</span> {currentAirline.uniform.style}
                  </p>
                </CardContent>
              </Card>

              {/* Hiring Requirements */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-500" />
                    İşe Alım Kriterleri
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {currentAirline.hiring.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-1" />
                        <span className="text-gray-600">{req}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Hiring Process */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-blue-500" />
                    İşe Alım Süreci
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {currentAirline.hiring.process.map((step, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
                          {i + 1}
                        </span>
                        <span className="text-gray-600">{step}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            </div>

            {/* Benefits */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Çalışan Avantajları
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {currentAirline.hiring.benefits.map((benefit, i) => (
                    <Badge key={i} className="bg-amber-100 text-amber-800 hover:bg-amber-200">
                      {benefit}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Culture (if available) */}
            {currentAirline.culture && (
              <Card className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-indigo-500" />
                    {currentAirline.culture.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {currentAirline.culture.points.map((point, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-indigo-500" />
                        <span className="text-gray-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </CabinCrewLayout>
  );
}
